import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardHeader, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Alert, CircularProgress, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemIcon, Divider, IconButton, Badge,
  Select, MenuItem, 
  FormControl, InputLabel, TextField,
  InputAdornment, Pagination, Stack
} from '@mui/material';
import {
  AdminPanelSettings, People, Settings, Notifications, 
  Assessment, CheckCircle,
  Download, Search, 
  
  Description as DescriptionIcon,
  History as HistoryIcon, Visibility,
  VisibilityOff, Edit, Refresh, TrendingUp,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';
import { useSnackbar } from 'notistack';

// Interfaces for Admin Panel data
interface AdminDashboardOverview {
  user_stats: {
    total_users: number;
    active_users: number;
    admin_users: number;
  };
  fund_stats: {
    total_funds: number;
    active_funds: number;
  };
  recent_actions: AdminAction[];
  system_metrics: SystemMetric[];
  unread_notifications: number;
}

interface AdminAction {
  id: string;
  admin_user_id: string;
  action_type: string;
  target_type: string;
  target_id: string;
  description: string;
  details: any;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

interface SystemMetric {
  id: string;
  metric_name: string;
  metric_value: number;
  metric_unit: string;
  metric_type: string;
  category: string;
  tags: any;
  recorded_at: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login: string;
}

interface SystemConfiguration {
  id: string;
  key: string;
  value: string;
  value_type: string;
  category: string;
  description: string;
  is_encrypted: boolean;
  is_public: boolean;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

interface AdminNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  priority: string;
  category: string;
  is_read: boolean;
  is_acknowledged: boolean;
  action_required: boolean;
  action_url: string;
  metadata: any;
  expires_at: string;
  created_at: string;
  read_at: string;
  acknowledged_at: string;
}

interface SystemLog {
  id: string;
  level: string;
  logger_name: string;
  message: string;
  module: string;
  function: string;
  line_number: number;
  user_id: string;
  session_id: string;
  request_id: string;
  ip_address: string;
  user_agent: string;
  extra_data: any;
  created_at: string;
}

interface SystemHealth {
  database: string;
  error_logs_last_hour: number;
  active_sessions: number;
  recent_metrics: number;
  status: string;
}

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Data states
  const [overview, setOverview] = useState<AdminDashboardOverview | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [configurations, setConfigurations] = useState<SystemConfiguration[]>([]);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);

  // Pagination states
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [actionsPage, setActionsPage] = useState(1);
  const [actionsTotal, setActionsTotal] = useState(0);

  // Filter states
  const [userSearch, setUserSearch] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userActive, setUserActive] = useState<boolean | null>(null);
  const [logLevel, setLogLevel] = useState('');
  const [logLogger, setLogLogger] = useState('');
  const [actionType, setActionType] = useState('');

  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<SystemConfiguration | null>(null);
  const [configValue, setConfigValue] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      loadOverview();
    } else if (activeTab === 1) {
      loadUsers();
    } else if (activeTab === 2) {
      loadConfigurations();
    } else if (activeTab === 3) {
      loadNotifications();
    } else if (activeTab === 4) {
      loadLogs();
    } else if (activeTab === 5) {
      loadMetrics();
    } else if (activeTab === 6) {
      loadActions();
    } else if (activeTab === 7) {
      loadSystemHealth();
    }
  }, [activeTab, usersPage, notificationsPage, logsPage, actionsPage]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await loadOverview();
      setLoading(false);
    } catch (error) {
      LoggerService.error('Error loading initial data:', error);
      setError('Failed to load admin data');
      setLoading(false);
    }
  };

  const loadOverview = async () => {
    try {
      const response = await api.get('/admin/dashboard/overview');
      setOverview(response.data);
    } catch (error) {
      LoggerService.error('Error loading overview:', error);
      enqueueSnackbar('Failed to load dashboard overview', { variant: 'error' });
    }
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams({
        skip: String((usersPage - 1) * 20),
        limit: '20'
      });
      
      if (userSearch) params.append('search', userSearch);
      if (userRole) params.append('role', userRole);
      if (userActive !== null) params.append('is_active', String(userActive));
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      LoggerService.error('Error loading users:', error);
      enqueueSnackbar('Failed to load users', { variant: 'error' });
    }
  };

  const loadConfigurations = async () => {
    try {
      const response = await api.get('/admin/config');
      setConfigurations(response.data);
    } catch (error) {
      LoggerService.error('Error loading configurations:', error);
      enqueueSnackbar('Failed to load system configurations', { variant: 'error' });
    }
  };

  const loadNotifications = async () => {
    try {
      const params = new URLSearchParams({
        skip: String((notificationsPage - 1) * 20),
        limit: '20'
      });
      
      const response = await api.get(`/admin/notifications?${params}`);
      setNotifications(response.data.notifications);
      setNotificationsTotal(response.data.total);
    } catch (error) {
      LoggerService.error('Error loading notifications:', error);
      enqueueSnackbar('Failed to load notifications', { variant: 'error' });
    }
  };

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams({
        skip: String((logsPage - 1) * 50),
        limit: '50'
      });
      
      if (logLevel) params.append('level', logLevel);
      if (logLogger) params.append('logger_name', logLogger);
      
      const response = await api.get(`/admin/logs?${params}`);
      setLogs(response.data.logs);
      setLogsTotal(response.data.total);
    } catch (error) {
      LoggerService.error('Error loading logs:', error);
      enqueueSnackbar('Failed to load system logs', { variant: 'error' });
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await api.get('/admin/metrics?hours=24');
      setMetrics(response.data);
    } catch (error) {
      LoggerService.error('Error loading metrics:', error);
      enqueueSnackbar('Failed to load system metrics', { variant: 'error' });
    }
  };

  const loadActions = async () => {
    try {
      const params = new URLSearchParams({
        skip: String((actionsPage - 1) * 50),
        limit: '50'
      });
      
      if (actionType) params.append('action_type', actionType);
      
      const response = await api.get(`/admin/actions?${params}`);
      setActions(response.data.actions);
      setActionsTotal(response.data.total);
    } catch (error) {
      LoggerService.error('Error loading actions:', error);
      enqueueSnackbar('Failed to load admin actions', { variant: 'error' });
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await api.get('/admin/health');
      setSystemHealth(response.data);
    } catch (error) {
      LoggerService.error('Error loading system health:', error);
      enqueueSnackbar('Failed to load system health', { variant: 'error' });
    }
  };

  const handleUserStatusToggle = async (userId: string, isActive: boolean) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { is_active: isActive });
      enqueueSnackbar(`User ${isActive ? 'activated' : 'deactivated'} successfully`, { variant: 'success' });
      loadUsers();
    } catch (error) {
      LoggerService.error('Error updating user status:', error);
      enqueueSnackbar('Failed to update user status', { variant: 'error' });
    }
  };

  const handleConfigUpdate = async () => {
    if (!selectedConfig) return;
    
    try {
      await api.put(`/admin/config/${selectedConfig.key}`, { value: configValue });
      enqueueSnackbar('Configuration updated successfully', { variant: 'success' });
      setConfigDialogOpen(false);
      loadConfigurations();
    } catch (error) {
      LoggerService.error('Error updating configuration:', error);
      enqueueSnackbar('Failed to update configuration', { variant: 'error' });
    }
  };

  const handleMarkNotificationRead = async (notificationId: string) => {
    try {
      await api.put(`/admin/notifications/${notificationId}/read`);
      enqueueSnackbar('Notification marked as read', { variant: 'success' });
      loadNotifications();
    } catch (error) {
      LoggerService.error('Error marking notification as read:', error);
      enqueueSnackbar('Failed to mark notification as read', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR': return 'error';
      case 'WARNING': return 'warning';
      case 'INFO': return 'info';
      case 'DEBUG': return 'default';
      default: return 'default';
    }
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'success': return 'success';
      default: return 'default';
    }
  };

  if (loading && !overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <AdminPanelSettings sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Admin Panel
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Dashboard" icon={<Assessment />} />
          <Tab label="Users" icon={<People />} />
          <Tab label="Configuration" icon={<Settings />} />
          <Tab label="Notifications" icon={<Notifications />} />
          <Tab label="System Logs" icon={<DescriptionIcon />} />
          <Tab label="Metrics" icon={<TrendingUp />} />
          <Tab label="Actions" icon={<HistoryIcon />} />
          <Tab label="System Health" icon={<CheckCircle />} />
        </Tabs>
      </Paper>

      {/* Dashboard Tab */}
      {activeTab === 0 && overview && (
        <Box>
          <Grid container spacing={3}>
            {/* User Statistics */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Users
                      </Typography>
                      <Typography variant="h4">
                        {overview.user_stats.total_users}
                      </Typography>
                    </Box>
                    <People color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Active Users
                      </Typography>
                      <Typography variant="h4">
                        {overview.user_stats.active_users}
                      </Typography>
                    </Box>
                    <CheckCircle color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Funds
                      </Typography>
                      <Typography variant="h4">
                        {overview.fund_stats.total_funds}
                      </Typography>
                    </Box>
                    <Assessment color="info" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Unread Notifications
                      </Typography>
                      <Typography variant="h4">
                        {overview.unread_notifications}
                      </Typography>
                    </Box>
                    <Badge badgeContent={overview.unread_notifications} color="error">
                      <Notifications color="warning" sx={{ fontSize: 40 }} />
                    </Badge>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Actions */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Recent Actions" />
                <CardContent>
                  <List>
                    {overview.recent_actions.slice(0, 5).map((action) => (
                      <ListItem key={action.id} divider>
                        <ListItemIcon>
                          <HistoryIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={action.description}
                          secondary={`${action.action_type} - ${new Date(action.created_at).toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* System Metrics Chart */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="System Metrics (24h)" />
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={overview.system_metrics.slice(0, 20)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="recorded_at" />
                      <YAxis />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="metric_value" stroke="#8884d8" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Users Tab */}
      {activeTab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search Users"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="">All Roles</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={userActive === null ? '' : String(userActive)}
                    onChange={(e) => setUserActive(e.target.value === '' ? null : e.target.value === 'true')}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadUsers}
                  startIcon={<Refresh />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleUserStatusToggle(user.id, !user.is_active)}
                        color={user.is_active ? 'error' : 'success'}
                      >
                        {user.is_active ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(usersTotal / 20)}
              page={usersPage}
              onChange={(_, page) => setUsersPage(page)}
            />
          </Box>
        </Box>
      )}

      {/* Configuration Tab */}
      {activeTab === 2 && (
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configurations.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>{config.key}</TableCell>
                    <TableCell>
                      {config.is_encrypted ? '***ENCRYPTED***' : config.value}
                    </TableCell>
                    <TableCell>{config.value_type}</TableCell>
                    <TableCell>{config.category}</TableCell>
                    <TableCell>{config.description}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedConfig(config);
                          setConfigValue(config.value);
                          setConfigDialogOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Configuration Edit Dialog */}
          <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Configuration</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Key"
                value={selectedConfig?.key || ''}
                disabled
                sx={{ mb: 2, mt: 1 }}
              />
              <TextField
                fullWidth
                label="Value"
                value={configValue}
                onChange={(e) => setConfigValue(e.target.value)}
                multiline
                rows={4}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleConfigUpdate} variant="contained">Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Notifications Tab */}
      {activeTab === 3 && (
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell>{notification.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={notification.notification_type}
                        color={getNotificationTypeColor(notification.notification_type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.priority}
                        color={notification.priority === 'high' ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.is_read ? 'Read' : 'Unread'}
                        color={notification.is_read ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(notification.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {!notification.is_read && (
                        <IconButton
                          onClick={() => handleMarkNotificationRead(notification.id)}
                          color="primary"
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(notificationsTotal / 20)}
              page={notificationsPage}
              onChange={(_, page) => setNotificationsPage(page)}
            />
          </Box>
        </Box>
      )}

      {/* System Logs Tab */}
      {activeTab === 4 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={logLevel}
                    onChange={(e) => setLogLevel(e.target.value)}
                    label="Level"
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    <MenuItem value="ERROR">Error</MenuItem>
                    <MenuItem value="WARNING">Warning</MenuItem>
                    <MenuItem value="INFO">Info</MenuItem>
                    <MenuItem value="DEBUG">Debug</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Logger Name"
                  value={logLogger}
                  onChange={(e) => setLogLogger(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadLogs}
                  startIcon={<Refresh />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Logger</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Chip
                        label={log.level}
                        color={getLogLevelColor(log.level) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.logger_name}</TableCell>
                    <TableCell>{log.message}</TableCell>
                    <TableCell>
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(logsTotal / 50)}
              page={logsPage}
              onChange={(_, page) => setLogsPage(page)}
            />
          </Box>
        </Box>
      )}

      {/* Metrics Tab */}
      {activeTab === 5 && (
        <Box>
          <Grid container spacing={3}>
            {metrics.map((metric) => (
              <Grid item xs={12} md={6} lg={4} key={metric.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {metric.metric_name}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {metric.metric_value} {metric.metric_unit}
                    </Typography>
                    <Typography color="textSecondary">
                      {metric.category} • {new Date(metric.recorded_at).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Actions Tab */}
      {activeTab === 6 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Action Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="user_created">User Created</MenuItem>
                    <MenuItem value="user_updated">User Updated</MenuItem>
                    <MenuItem value="fund_updated">Fund Updated</MenuItem>
                    <MenuItem value="system_config_changed">System Config Changed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadActions}
                  startIcon={<Refresh />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.map((action) => (
                  <TableRow key={action.id}>
                    <TableCell>
                      <Chip label={action.action_type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={action.target_type} size="small" color="secondary" />
                    </TableCell>
                    <TableCell>{action.description}</TableCell>
                    <TableCell>{action.ip_address}</TableCell>
                    <TableCell>
                      {new Date(action.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(actionsTotal / 50)}
              page={actionsPage}
              onChange={(_, page) => setActionsPage(page)}
            />
          </Box>
        </Box>
      )}

      {/* System Health Tab */}
      {activeTab === 7 && systemHealth && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="System Status" />
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Chip
                      label={systemHealth.status.toUpperCase()}
                      color={getStatusColor(systemHealth.status) as any}
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body1">
                      Overall system health
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Database Status
                    </Typography>
                    <Chip
                      label={systemHealth.database.toUpperCase()}
                      color={systemHealth.database === 'healthy' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Error Logs (Last Hour)
                    </Typography>
                    <Typography variant="h6">
                      {systemHealth.error_logs_last_hour}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Active Sessions
                    </Typography>
                    <Typography variant="h6">
                      {systemHealth.active_sessions}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Recent Metrics
                    </Typography>
                    <Typography variant="h6">
                      {systemHealth.recent_metrics}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Quick Actions" />
                <CardContent>
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={loadSystemHealth}
                    >
                      Refresh Health Status
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => {
                        const reportData = JSON.stringify({ users: stats, generatedAt: new Date().toISOString() }, null, 2);
                        const blob = new Blob([reportData], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `system-report-${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        enqueueSnackbar('System report exported', { variant: 'success' });
                      }}
                    >
                      Export System Report
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Settings />}
                      onClick={() => setActiveTab(2)}
                    >
                      System Configuration
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default AdminPage;